// components/ExportButton.tsx
import React, { useState, useEffect } from 'react'
import { Button, ProgressBar } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { downloadExport } from '../../utils/fileDownloader'

interface ExportButtonProps {
  exportId: string
  uri?: string
  filename?: string
  className?: string
  showProgress?: boolean
  size?: 'sm' | 'lg'
  children?: React.ReactNode
}

const ExportButton: React.FC<ExportButtonProps> = ({
  exportId,
  uri,
  filename,
  className,
  size,
  children,
}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Use the accessToken from the auth slice
  const accessToken = useSelector((state: RootState) => state.auth.accessToken)
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)

  // Reset error state when exportId changes
  useEffect(() => {
    setError(null)
  }, [exportId])

  const handleExport = () => {
    if (!isAuthenticated || !accessToken) {
      setError('Authentication required. Please log in again.')
      return
    }

    setIsDownloading(true)
    setProgress(0)
    setError(null)

    downloadExport({
      exportId,
      uri,
      accessToken,
      filename,
      onProgress: (progressValue: number) => {
        setProgress(progressValue)
      },
      onComplete: () => {
        setIsDownloading(false)
        setProgress(100)
      },
      onError: (error: Error) => {
        console.error('Download failed:', error)
        setIsDownloading(false)
        setError(error.message)
      },
    })
  }

  return (
    <div className={className} style={{ position: 'relative' }}>
      {isDownloading ? (
        <ProgressBar
          now={progress}
          label={`${Math.round(progress)}%`}
          style={{ height: size === 'sm' ? '31px' : '38px' }}
        />
      ) : (
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={!isAuthenticated}
          size={size}
          className="w-100"
        >
          {children || 'Download Export'}
        </Button>
      )}

      {error && <div className="text-danger mt-2 small">{error}</div>}
    </div>
  )
}

export default ExportButton
